import {
  formatMoney, formatMiles, formatDistance, priceChange, relativeTime,
  TAG_LABELS, ageLabel, mapsUrl, isFollowUpDue, STATUS_LABELS, LINK_HEALTH_INFO,
} from '../util.js'
import StatusMenu from './StatusMenu.jsx'

export default function ListingCard({ listing, note = {}, onOpen, onStatus, showDealer = true }) {
  const change = priceChange(listing)
  const distance = formatDistance(listing.distanceMiles)
  const maps = mapsUrl(listing)
  const age = ageLabel(listing)
  // 'unchecked' is a display-only fallback, not a state the crawler ever
  // writes (see LINK_HEALTH_STATES) — a never-checked link otherwise rendered
  // nothing at all, indistinguishable from a card that simply omits the chip.
  const healthState = listing.linkHealth?.state || 'unchecked'
  const health = LINK_HEALTH_INFO[healthState]
  const status = note?.status || ''
  const followUpDue = isFollowUpDue(note)

  // The card body is a button, not an anchor: it opens the detail sheet, and
  // the real links inside it could not legally be nested in an anchor.
  //
  // The button content model permits phrasing content only, so everything
  // that used to structure this — div/dl/ul/p, and the h3 — is a span here
  // instead (Task 15 supplies the CSS and sets `display: block` on them
  // there; leave them as spans rather than "tidying" them back into block
  // elements). The former h3 carries `role="heading" aria-level="3"` so it
  // keeps its heading semantics without being heading content itself. The
  // button also carries an explicit aria-label: without one its accessible
  // name would otherwise be the concatenation of every string in the card.
  return (
    // matchTier can be absent on a listing the crawler hasn't tiered yet;
    // falling back to 'none' keeps this a real, styled tier class rather than
    // the meaningless `tier-undefined`.
    <article className={`card tier-${listing.matchTier || 'none'}`}>
      <button
        className="card-body"
        aria-label={`Open details for ${listing.title}`}
        onClick={() => onOpen?.(listing)}
      >
        <span className="card-head">
          <span role="heading" aria-level="3">{listing.year} {listing.trim}</span>
          {listing.matchTier === 'full' && <span className="star" aria-label="Full match">★</span>}
        </span>

        <span className="price-row">
          <span className="price">{formatMoney(listing.price)}</span>
          {change && (
            <span className={change.dropped ? 'delta down' : 'delta up'}>
              {change.dropped ? '↓' : '↑'} {formatMoney(Math.abs(change.delta))}
              <span className="was"> from {formatMoney(change.from)}</span>
            </span>
          )}
        </span>

        <span className="facts">
          <span>
            <span>Mileage</span>
            <span>{formatMiles(listing.mileage)}</span>
          </span>
          <span>
            <span>Location</span>
            <span>
              {listing.location || '—'}
              {distance && <span className="muted"> · {distance}</span>}
            </span>
          </span>
          {showDealer && listing.dealerName && (
            <span>
              <span>Dealer</span>
              <span>{listing.dealerName}</span>
            </span>
          )}
          {listing.stockNumber && (
            <span>
              <span>Stock</span>
              <span className="stock">{listing.stockNumber}</span>
            </span>
          )}
        </span>

        {listing.matchTags?.length > 0 && (
          <span className="tags">
            {listing.matchTags.map((t) => <span key={t}>{TAG_LABELS[t] || t}</span>)}
          </span>
        )}

        {listing.matchTier === 'none' && listing.matchReasons?.length > 0 && (
          <span className="reasons">{listing.matchReasons.join(' · ')}</span>
        )}
      </button>

      <div className="card-actions">
        {listing.dealerUrl ? (
          <a href={listing.dealerUrl} target="_blank" rel="noreferrer">Dealer ↗</a>
        ) : (
          listing.listingUrl && (
            <a className="muted" href={listing.listingUrl} target="_blank" rel="noreferrer">
              MotoHunt ↗
            </a>
          )
        )}
        {maps && <a href={maps} target="_blank" rel="noreferrer">Map ↗</a>}
        <span className={`health h-${healthState}`} title={health.text}>{health.icon}</span>
        {followUpDue && <span className="followup-due">Follow-up due</span>}
        <span className="spacer" />
        {status && <span className={`status-pill s-${status}`}>{STATUS_LABELS[status]}</span>}
        <StatusMenu onStatus={(s) => onStatus?.(listing, s)} />
      </div>

      <footer>
        {age && <span>{age}</span>}
        <span>First seen {relativeTime(listing.firstSeenAt)}</span>
        {listing.status === 'delisted' && <span className="gone">Delisted</span>}
        {listing.priceHistory?.length > 1 && <span>{listing.priceHistory.length} price points</span>}
        {listing.vin && <span className="vin">{listing.vin}</span>}
      </footer>
    </article>
  )
}
